import React, { useState, useEffect } from "react";
import api from "../../setupMiddleware";
import useApiLoader from "../../Utils/useApiLoader";
import Loader from "../Loader/Loader";
import { getUser, hasPermission } from "../../Utils/auth";
import PlusIcon from "../../assets/icons/plus";
import Popup from "../Common/Popup";
import { ToastContainer, toast, Bounce } from "react-toastify";
import CustomSelect from "../Common/CustomSelect";
import Input from "../Common/CustomInput";
import Textarea from "../Common/Textarea";
import EmployeDocumentTable from "../Common/EmployeDocumentTable";

const RequestDocument = () => {
  const loading = useApiLoader();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [docReq, setDocReq] = useState([]);
  const [documentReq, setDocumentReq] = useState({
    document_type: "",
    bank_name: "",
    reason: "",
    subject: "",
    to_whom: "",
  });
  const { document_type, bank_name, reason, subject, to_whom } = documentReq;
  const loggedUser = getUser();
  const documentOptions = [
    {
      label: "Salary Certificate",
      value: "salary_certificate",
      location: 1,
    },
    {
      label: "Salary transfer",
      value: "salary_transfer",
      location: 1,
    },
    {
      label: "NOC",
      value: "noc",
      location: 1,
    },
  ];

  const getAllRequests = async () => {
    api
      .get("/api/request-documents")
      .then((res) => {
        if (res && res.status === 200) {
          setDocReq(res.data?.data);
        }
      })
      .catch((error) => {
        toast.error(error.response.data.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      });
  };
  function getDocumentsByLocation(locationId) {
    return documentOptions.filter((doc) => doc.location === locationId);
  }

  const handleCloseRequest = () => {
    setOpen(false);
    setDocumentReq({
      document_type: "",
      bank_name: "",
      reason: "",
      subject: "",
      to_whom: "",
    });
  };

  const validateFields = () => {
    const newErrors = {};

    if (!document_type) newErrors.document_type = ["Document Type is Required"];
    if (document_type === "salary_transfer" && !reason)
      newErrors.reason = ["Reason is Required"];
    if (document_type === "salary_transfer" && !bank_name)
      newErrors.bank_name = ["Bank Name is Required"];
    if (document_type === "noc" && !reason)
      newErrors.reason = ["Reason is Required"];
    if (!subject) newErrors.subject = ["Subject is Required"];
    if (!to_whom) newErrors.to_whom = ["To Whom is Required"];

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetErrors = () => setFieldErrors({});

  const handleSubmitRequest = async () => {
    resetErrors();

    // run frontend validation
    if (!validateFields()) {
      window.scrollTo(0, 0);
      return; // stop if errors exist
    }
    await api
      .post(`/api/request-documents`, documentReq)
      .then((res) => {
        if (res && res.status === 200) {
          toast.success(res.data?.message, {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
          });
          setOpen(false);
          getAllRequests();
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.message, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
        });
      });
  };

  useEffect(() => {
    getAllRequests();
  }, []);

  return (
    <>
      {loading && <Loader opacity={90} fullscreen />}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full mx-auto px-4 py-3 gap-4">
        {/* Left Content */}
        <p>
          <span className="block text-xl sm:text-2xl font-medium text-[#de2b26]">
            Request Documents
          </span>
          <span className="block text-sm text-[#142b70]/50">
            Create or view document request
          </span>
        </p>

        {/* Right Button */}
        <div className="flex">
          {hasPermission("request_employee_documents_request") && (
            <button
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded bg-[#de2b26] hover:bg-[#c30f0a] transition text-white text-sm font-medium"
            >
              <PlusIcon iconClass="w-4 h-4" color="#ffffff" />
              Request Document
            </button>
          )}

          {/* Delete Popup */}
          <Popup
            isOpen={open}
            onClose={handleCloseRequest}
            title="Request Document"
          >
            <div className="flex flex-col gap-6 mt-[30px]">
              <div className="flex flex-col gap-2">
                <p className="text-sm text-[#142b70]/80">Document Type</p>
                <CustomSelect
                  options={getDocumentsByLocation(loggedUser?.location_id)}
                  value={document_type}
                  placeholder="Document Type"
                  width="w-full"
                  height="h-[46px]"
                  fontSize="14px"
                  onChange={(op) => {
                    setDocumentReq({
                      ...documentReq,
                      document_type: op.value,
                    });
                    if (fieldErrors.document_type) {
                      setFieldErrors({
                        ...fieldErrors,
                        document_type: undefined,
                      });
                    }
                  }}
                  error={Boolean(fieldErrors.document_type)}
                  helperText={fieldErrors?.document_type?.[0]}
                />
              </div>
              <div className="flex flex-col gap-2 w-full">
                <p className="text-sm text-[#142b70]/80">Subject</p>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Enter Subject"
                    value={subject}
                    onChange={(e) => {
                      setDocumentReq({
                        ...documentReq,
                        subject: `${e.target.value}`,
                      });
                      if (fieldErrors.subject) {
                        setFieldErrors({
                          ...fieldErrors,
                          subject: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.subject)}
                    helperText={fieldErrors?.subject?.[0]}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full">
                <p className="text-sm text-[#142b70]/80">To Whom</p>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Enter To Whom"
                    value={to_whom}
                    onChange={(e) => {
                      setDocumentReq({
                        ...documentReq,
                        to_whom: `${e.target.value}`,
                      });
                      if (fieldErrors.to_whom) {
                        setFieldErrors({
                          ...fieldErrors,
                          to_whom: undefined,
                        });
                      }
                    }}
                    error={Boolean(fieldErrors.to_whom)}
                    helperText={fieldErrors?.to_whom?.[0]}
                  />
                </div>
              </div>
              {document_type && (
                <div className="flex flex-col gap-6 mb-[30px]">
                  {document_type === "salary_transfer" && (
                    <>
                      <div className="flex flex-col gap-2 w-full">
                        <p className="text-sm text-[#142b70]/80">Bank Name</p>
                        <div className="relative">
                          <Input
                            type="text"
                            placeholder="Enter Bank name"
                            value={bank_name}
                            onChange={(e) => {
                              setDocumentReq({
                                ...documentReq,
                                bank_name: `${e.target.value}`,
                              });
                              if (fieldErrors.bank_name) {
                                setFieldErrors({
                                  ...fieldErrors,
                                  bank_name: undefined,
                                });
                              }
                            }}
                            error={Boolean(fieldErrors.bank_name)}
                            helperText={fieldErrors?.bank_name?.[0]}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 w-full">
                        <p className="text-sm text-[#142b70]/80">Reason</p>
                        <div className="relative">
                          <Textarea
                            placeholder="Enter Reason"
                            value={reason}
                            onChange={(e) => {
                              setDocumentReq({
                                ...documentReq,
                                reason: `${e.target.value}`,
                              });
                              if (fieldErrors.bank_name) {
                                setFieldErrors({
                                  ...fieldErrors,
                                  reason: undefined,
                                });
                              }
                            }}
                            error={Boolean(fieldErrors.reason)}
                            helperText={fieldErrors?.reason?.[0]}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {document_type === "noc" && (
                    <>
                      <div className="flex flex-col gap-2 w-full">
                        <p className="text-sm text-[#142b70]/80">Reason</p>
                        <div className="relative">
                          <Textarea
                            placeholder="Enter Reason"
                            value={reason}
                            onChange={(e) => {
                              setDocumentReq({
                                ...documentReq,
                                reason: `${e.target.value}`,
                              });
                              if (fieldErrors.bank_name) {
                                setFieldErrors({
                                  ...fieldErrors,
                                  reason: undefined,
                                });
                              }
                            }}
                            error={Boolean(fieldErrors.reason)}
                            helperText={fieldErrors?.reason?.[0]}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={handleCloseRequest}
                className="px-10 py-3.5 rounded-md border border-[#de2b26] text-[#de2b26] font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRequest}
                className="px-10 py-3.5 rounded-md bg-[#de2b26] text-white font-medium disabled:opacity-50"
              >
                Request
              </button>
            </div>
          </Popup>
        </div>
      </div>

      <div className="w-full mx-auto px-4 py-3 relative">
        <div className="w-full mx-auto px-4 py-3 relative rounded-xl bg-white border border-[#a48572]/10">
          <div className="flex flex-col justify-start items-start gap-[20px]">
            <div className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 relative gap-5">
              <p className="flex-grow-0 flex-shrink-0 text-base font-medium text-left text-[#142b70]">
                Document Requests
              </p>
            </div>
            <EmployeDocumentTable data={docReq} />
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default RequestDocument;
